import React, { useEffect, useRef } from 'react';
import { Text, View } from '@react-pdf/renderer';
import { en, Locale } from '../../locales';
import getWorkerProtectionTableRows from '../../helpers/tableConfigs/workerProtectionTableConfig';
import parentStyles from '../../utils/parentStyles';
import FlexTable from '../FlexTable';
import styles from '../FlexTable/styles';

interface WorkerProtectionData {
  productName: string;
  signalword: string;
  rup: string;
  ppe: string;
  ppereentry: string;
  key: string;
}

interface WorkerProtectionProps {
  workerProtectionData: WorkerProtectionData[];
  locale?: Locale;
}

const WorkerProtection: React.FC<WorkerProtectionProps> = ({
  workerProtectionData,
  locale = en
}) => {
  const firstPage = useRef(-1);
  const lastPage = useRef(-1);
  const isLastPageSet = useRef(false);
  const isFirstRender = useRef(true);

  const filteredData = workerProtectionData.filter(Boolean);
  const firstTable = filteredData[0];
  const remainingTables = filteredData.slice(1);

  useEffect(() => {
    firstPage.current = -1;
    lastPage.current = -1;
    isLastPageSet.current = false;
    isFirstRender.current = true;
  }, [workerProtectionData]);

  return (
    <View>
      <View
        fixed
        render={({ pageNumber }) => {
          const _firstPage = firstPage.current;

          // Only show fixed title on pages after the first page
          if (_firstPage === -1 || pageNumber <= _firstPage) {
            return null;
          }

          return (
            <View style={parentStyles.title}>
              <Text
                style={{
                  fontFamily: 'Noto Sans',
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#14151C',
                  paddingLeft: 4
                }}
              >
                {locale.worker_protection.title}{' '}
                <Text style={styles.headerTextContinued}>
                  {locale.common.continued}
                </Text>
              </Text>
            </View>
          );
        }}
      />
      <View>
        {/* First table with title */}
        {firstTable && (
          <View>
            <View style={parentStyles.title}>
              <Text style={parentStyles.titleText}>
                {locale.worker_protection.title}
              </Text>
            </View>
            {/* Track where the actual content renders */}
            <Text
              render={({ pageNumber }) => {
                // Check if we are rendering this for the first time
                if (isFirstRender.current) {
                  // If the renderer has returned to a previous page, we hard-set the first page to that page
                  if (pageNumber <= firstPage.current) {
                    isFirstRender.current = false;
                  }

                  firstPage.current = pageNumber;
                }
                return null;
              }}
            />
            <FlexTable
              tableRows={getWorkerProtectionTableRows(firstTable, locale)}
              cellStyles={{ minHeight: 24 }}
              rowBordered={false}
            />
          </View>
        )}
        {/* Remaining tables */}
        {remainingTables.map((data, index) => (
          <View key={data.key || index}>
            <FlexTable
              tableRows={getWorkerProtectionTableRows(data, locale)}
              cellStyles={{ minHeight: 24 }}
              rowBordered={false}
            />
          </View>
        ))}
        {/* Track the last page of this section */}
        <Text
          render={({ pageNumber }) => {
            if (!isLastPageSet.current || pageNumber > lastPage.current) {
              lastPage.current = pageNumber;
              isLastPageSet.current = true;
            }
            return null;
          }}
        />
      </View>
      {/* Continuation indicator at bottom of each page except the last */}
      <View
        render={({ pageNumber }) => {
          const _firstPage = firstPage.current;
          const _lastPage = lastPage.current;

          // Don't show if we're on the last page or if we haven't determined pages yet
          if (
            _lastPage === -1 ||
            _firstPage === -1 ||
            pageNumber >= _lastPage
          ) {
            return null;
          }

          // Only show if we're within the section and not on the last page
          if (pageNumber >= _firstPage && pageNumber < _lastPage) {
            return (
              <View style={styles.continuedTextWrapper}>
                <Text style={styles.continuedText}>
                  {locale.worker_protection.continued}
                </Text>
              </View>
            );
          }

          return null;
        }}
        fixed
      />
    </View>
  );
};

export default WorkerProtection;
